/**
 * Table filters in the URL, one param per value: `?filter.<columnKey>=<value>`. Links (e.g. from
 * dashboard widgets) use them to open a filtered table; `Views` applies them once and removes them.
 */
const FILTER_SEARCH_PARAM_PREFIX = 'filter.';

const MAX_FILTER_SEARCH_PARAMS = 50;
const MAX_FILTER_SEARCH_PARAMS_LENGTH = 4000;

/** URL filter values by column key. */
export type TUrlFilters = Record<string, string[]>;

const getFilterKey = (paramName: string) =>
  paramName.startsWith(FILTER_SEARCH_PARAM_PREFIX)
    ? paramName.slice(FILTER_SEARCH_PARAM_PREFIX.length)
    : undefined;

export const buildFilterSearchParams = (filters: TUrlFilters): URLSearchParams =>
  new URLSearchParams(
    Object.entries(filters).flatMap(([key, values]) =>
      values.map((value) => [`${FILTER_SEARCH_PARAM_PREFIX}${key}`, value])
    )
  );

export const hasFilterSearchParams = (params: URLSearchParams) =>
  Array.from(params.keys()).some((name) => getFilterKey(name) !== undefined);

export const removeFilterSearchParams = (params: URLSearchParams): URLSearchParams =>
  new URLSearchParams(Array.from(params).filter(([name]) => getFilterKey(name) === undefined));

/**
 * Reads the `filter.*` params. Empty keys and values are skipped; no filters, or too many or too
 * long params, give `undefined`.
 */
export const parseFilterSearchParams = (params: URLSearchParams): TUrlFilters | undefined => {
  const entries = Array.from(params).flatMap(([name, value]) => {
    const key = getFilterKey(name);
    return key === undefined ? [] : [[key, value] as const];
  });
  const length = entries.reduce((sum, [key, value]) => sum + key.length + value.length, 0);
  if (entries.length > MAX_FILTER_SEARCH_PARAMS || length > MAX_FILTER_SEARCH_PARAMS_LENGTH) {
    return undefined;
  }

  const filters = entries.reduce<TUrlFilters>(
    (acc, [key, value]) =>
      key && value && !acc[key]?.includes(value)
        ? { ...acc, [key]: [...(acc[key] ?? []), value] }
        : acc,
    {}
  );

  return Object.keys(filters).length ? filters : undefined;
};
